import React, { useState, useEffect, useRef, useCallback } from 'react';
import { customFetch, formatSQL, getDataLineage, exportFullLineageAsCsv } from '../utils/api';
import {
    Form,
    Select,
    Upload,
    Button,
    Typography,
    Card,
    notification,
    Progress,
    Row,
    Col,
    Statistic,
    Alert,
    List,
    Pagination,
    Descriptions,
    Tag,
    Spin,
    Divider,
    DatePicker,
    InputNumber,
    Input,
    Space,
    Tabs,
    Collapse,
} from 'antd';
import { UploadOutlined, EyeOutlined, SearchOutlined, ClearOutlined, CopyOutlined, FileTextOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

interface ProcessingSummary {
    // SQLFlow specific fields
    total_zip_files?: number;
    processed_zip_files?: number;
    
    // SQLFormatter specific fields
    log_file_name?: string;
    total_lines?: number;
    processed_lines_count?: number;
    valid_sql_lines?: number;
    error_lines_count?: number;
    lines_per_second?: number;
    
    // Common fields
    new_sqls_count: number;
    updated_sqls_count: number;
    total_sql_records?: number;  // New field for sum of count field
    processing_duration_seconds: number;
    processing_start_time?: string;
}

interface ProgressData {
    status: 'starting' | 'counting_lines' | 'processing' | 'completed' | 'error';
    progress_percentage: number;
    message: string;
    
    // SQLFlow progress fields
    total_files?: number;
    processed_files?: number;
    
    // SQLFormatter progress fields
    total_lines?: number;
    processed_lines?: number;
    valid_sql_lines?: number;
    log_file_name?: string;
    
    summary?: ProcessingSummary;
    error?: string;
}

interface SqlLogRecord {
    id: number;
    create_time: string;
    insert_time: string;
    update_time: string;
    count: number;
    sql_md5: string;
    sql_length: number;
    db: string;
    source: string;
    user_auth_id?: string;
    retvalue?: number;
    duration?: number;
}

interface SqlLogDetail extends SqlLogRecord {
    sqltext: string;
}

interface SqlLogListResponse {
    count: number;
    next: string | null;
    previous: string | null;
    results: SqlLogRecord[];
}

interface FilterOptions {
    databases: string[];
    sources: string[];
}

interface SearchFilters {
    create_time_start?: string;
    create_time_end?: string;
    database?: string;
    source?: string;
    sql_length_min?: number;
    duration_min?: number;
    user_auth_id?: string;
}

interface S3File {
    name: string;
    full_path: string;
    size: number;
    last_modified: string;
    file_timestamp?: string;
}

interface S3FileListResponse {
    log_type: string;
    prefix: string;
    files: S3File[];
}

const SqlCollectorPage: React.FC = () => {
    const [form] = Form.useForm();
    const [searchForm] = Form.useForm();
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [uploading, setUploading] = useState(false);
    const [summary, setSummary] = useState<ProcessingSummary | null>(null);
    const [progress, setProgress] = useState(0);
    const [progressMessage, setProgressMessage] = useState('');
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
    const progressIntervalRef = useRef<number | null>(null);

    // SQL logs records state
    const [sqlLogs, setSqlLogs] = useState<SqlLogRecord[]>([]);
    const [logsLoading, setLogsLoading] = useState(true);
    const [logsError, setLogsError] = useState<string | null>(null);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    
    // Tab state for SQL details
    const [activeTabKey, setActiveTabKey] = useState<string>('main');
    const [tabPanes, setTabPanes] = useState<Array<{
        key: string;
        title: string;
        loading: boolean;
        content: SqlLogDetail | null;
    }>>([]);

    // SQL editing state
    const [editedSqlText, setEditedSqlText] = useState<{[key: string]: string}>({});
    const [formatLoading, setFormatLoading] = useState<{[key: string]: boolean}>({});
    
    // Data lineage state
    const [lineageData, setLineageData] = useState<{[key: string]: any}>({});
    const [lineageLoading, setLineageLoading] = useState<{[key: string]: boolean}>({});
    const [lineageVisible, setLineageVisible] = useState<{[key: string]: boolean}>({});
    
    // CSV lineage state
    const [csvLineageData, setCsvLineageData] = useState<{[key: string]: any}>({});
    const [csvLineageLoading, setCsvLineageLoading] = useState<{[key: string]: boolean}>({});
    const [csvLineageVisible, setCsvLineageVisible] = useState<{[key: string]: boolean}>({});
    
    // Summary state
    const [summaryData, setSummaryData] = useState<{[key: string]: string}>({});
    const [summaryLoading, setSummaryLoading] = useState<{[key: string]: boolean}>({});
    const [summaryVisible, setSummaryVisible] = useState<{[key: string]: boolean}>({});
    const [selectedLlmModel, setSelectedLlmModel] = useState<{[key: string]: string}>({});
    
    // Available LLM models state
    const [availableModels, setAvailableModels] = useState<Array<{
        id: string;
        name: string;
        description: string;
        provider: string;
        temperature: number;
        max_tokens: number;
    }>>([]);
    const [modelsLoading, setModelsLoading] = useState(false);

    // Initialize default LLM model for new tabs
    const initializeDefaultLlmModel = (tabKey: string) => {
        if (!selectedLlmModel[tabKey] && availableModels.length > 0) {
            setSelectedLlmModel(prev => ({ 
                ...prev, 
                [tabKey]: availableModels[0].id 
            }));
        }
    };

    // Filter state
    const [filterOptions, setFilterOptions] = useState<FilterOptions>({ databases: [], sources: [] });
    const [currentFilters, setCurrentFilters] = useState<SearchFilters>({});
    const [searching, setSearching] = useState(false);

    // S3 files state
    const [s3Files, setS3Files] = useState<S3File[]>([]);
    const [s3Loading, setS3Loading] = useState(false);
    const [selectedS3File, setSelectedS3File] = useState<string | undefined>(undefined);

    const fetchFilterOptions = useCallback(async () => {
        try {
            const response = await customFetch('/api/sql-collector/logs/filter-options/');
            if (response.ok) {
                const data: FilterOptions = await response.json();
                setFilterOptions(data);
            }
        } catch (err) {
            console.error('Failed to fetch filter options:', err);
        }
    }, []);

    const fetchAvailableModels = useCallback(async () => {
        try {
            setModelsLoading(true);
            const response = await customFetch('/api/llm/available-models/');
            if (response.ok) {
                const data = await response.json();
                setAvailableModels(data.models || []);
            } else {
                console.error('Failed to fetch available models');
                setAvailableModels([]);
            }
        } catch (err) {
            console.error('Failed to fetch available models:', err);
            setAvailableModels([]);
        } finally {
            setModelsLoading(false);
        }
    }, []);

    const buildQueryParams = (filters: SearchFilters, page = 1, pageSize = 20) => {
        const params = new URLSearchParams({
            page: page.toString(),
            page_size: pageSize.toString(),
        });

        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                params.append(key, value.toString());
            }
        });

        return params.toString();
    };

    const fetchSqlLogs = useCallback(async (page = 1, pageSize = 20, filters: SearchFilters = {}) => {
        try {
            setLogsLoading(true);
            const queryParams = buildQueryParams(filters, page, pageSize);
            const response = await customFetch(`/api/sql-collector/logs/?${queryParams}`);
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to fetch SQL logs.');
            }
            const data: SqlLogListResponse = await response.json();
            setSqlLogs(data.results || []);
            setPagination(prev => ({ ...prev, total: data.count, current: page }));
            setLogsError(null);
        } catch (err: any) {
            setLogsError(err.message || 'Could not load SQL logs.');
            setSqlLogs([]);
            setPagination(prev => ({ ...prev, total: 0 }));
        } finally {
            setLogsLoading(false);
            setSearching(false);
        }
    }, []);

    const fetchLogDetail = async (logId: number, tabKey: string) => {
        try {
            // Set loading state for the specific tab
            setTabPanes(prev => prev.map(tab => 
                tab.key === tabKey ? { ...tab, loading: true } : tab
            ));
            
            const response = await customFetch(`/api/sql-collector/logs/${logId}/`);
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to fetch log details.');
            }
            const data: SqlLogDetail = await response.json();
            
            // Update the tab with the fetched data
            setTabPanes(prev => prev.map(tab => 
                tab.key === tabKey ? { ...tab, loading: false, content: data } : tab
            ));
            
            // Initialize default LLM model for this tab
            initializeDefaultLlmModel(tabKey);
        } catch (err: any) {
            // Remove the tab on error
            setTabPanes(prev => prev.filter(tab => tab.key !== tabKey));
            
            notification.error({
                message: 'Error',
                description: err.message || 'Could not load log details.',
            });
        }
    };

    const handleLogClick = (log: SqlLogRecord) => {
        const tabKey = `detail-${log.id}`;
        const existingTab = tabPanes.find(tab => tab.key === tabKey);
        
        if (existingTab) {
            // Tab already exists, just switch to it
            setActiveTabKey(tabKey);
        } else {
            // Create new tab
            const newTab = {
                key: tabKey,
                title: `SQL Details #${log.id}`,
                loading: true,
                content: null
            };
            
            setTabPanes(prev => [...prev, newTab]);
            setActiveTabKey(tabKey);
            
            // Fetch the detail data
            fetchLogDetail(log.id, tabKey);
        }
    };

    const handleTabClose = (targetKey: string) => {
        setTabPanes(prev => {
            const newPanes = prev.filter(pane => pane.key !== targetKey);
            
            // If closing the active tab, switch to the main tab or the last remaining tab
            if (targetKey === activeTabKey) {
                if (newPanes.length > 0) {
                    setActiveTabKey(newPanes[newPanes.length - 1].key);
                } else {
                    setActiveTabKey('main');
                }
            }
            
            return newPanes;
        });
    };

    const handleTabChange = (key: string) => {
        setActiveTabKey(key);
    };

    // Handle SQL formatting
    const handleFormatSQL = async (tabKey: string, sqlText: string, database: string) => {
        if (!sqlText.trim()) {
            notification.warning({
                message: 'Warning',
                description: 'SQL text is empty. Please enter some SQL to format.',
            });
            return;
        }

        // Warn about very large SQL texts
        if (sqlText.length > 10000) {
            notification.info({
                message: 'Large SQL Detected',
                description: `Your SQL is ${sqlText.length.toLocaleString()} characters long. Very large SQL texts may take longer to process or may fail to format.`,
                duration: 4,
            });
        }

        // Show which database vendor is being used for formatting
        const dbvendor = database.startsWith('dbv') ? database : `dbv${database}`;
        notification.info({
            message: 'Formatting SQL',
            description: `Using database vendor: ${dbvendor} (detected from record: ${database})`,
            duration: 3,
        });

        setFormatLoading(prev => ({ ...prev, [tabKey]: true }));
        
        try {
            const formData = new FormData();
            formData.append('sqltext', sqlText);
            formData.append('dbvendor', dbvendor);
            
            const result = await formatSQL(formData);
            
            if (result.formattedSQL) {
                setEditedSqlText(prev => ({ ...prev, [tabKey]: result.formattedSQL }));
                notification.success({
                    message: 'SQL Formatted Successfully',
                    description: `SQL formatted using ${dbvendor} dialect. The formatted SQL is displayed for viewing. Original SQL remains unchanged in the database.`,
                    duration: 4,
                });
            } else {
                throw new Error('No formatted SQL returned from server');
            }
        } catch (error: any) {
            let errorMessage = 'Failed to format SQL. Please try again.';
            
            // Handle specific error types
            if (error.message) {
                if (error.message.includes('SQL parsing failed')) {
                    errorMessage = `The SQL could not be parsed for ${dbvendor} dialect. Please check your SQL syntax and try again.`;
                } else if (error.message.includes('parse sql failed')) {
                    errorMessage = `The SQL syntax is invalid or unsupported for ${dbvendor} dialect. Please verify your SQL and try again.`;
                } else if (error.message.includes('too large to format')) {
                    errorMessage = 'The SQL text is too large to format. Consider splitting it into smaller parts or simplifying the query.';
                } else if (error.message.includes('Request-URI Too Large')) {
                    errorMessage = 'The SQL text is too large. Please try with a smaller SQL statement.';
                } else {
                    errorMessage = error.message;
                }
            }
            
            notification.error({
                message: 'Format Error',
                description: errorMessage,
                duration: 5, // Show error for 5 seconds
            });
        } finally {
            setFormatLoading(prev => ({ ...prev, [tabKey]: false }));
        }
    };

    // Handle Data Lineage analysis
    const handleDataLineage = async (tabKey: string, sqlText: string, database: string) => {
        if (!sqlText.trim()) {
            notification.warning({
                message: 'Warning',
                description: 'SQL text is empty. Please enter some SQL to analyze.',
            });
            return;
        }

        // Show which database vendor is being used for lineage analysis
        const dbvendor = database.startsWith('dbv') ? database : `dbv${database}`;
        notification.info({
            message: 'Analyzing Data Lineage',
            description: `Using database vendor: ${dbvendor} (detected from record: ${database})`,
            duration: 3,
        });

        setLineageLoading(prev => ({ ...prev, [tabKey]: true }));
        
        try {
            const formData = new FormData();
            formData.append('sqltext', sqlText);
            formData.append('dbvendor', dbvendor);
            
            const result = await getDataLineage(formData);
            
            if (result) {
                setLineageData(prev => ({ ...prev, [tabKey]: result }));
                setLineageVisible(prev => ({ ...prev, [tabKey]: true }));
                notification.success({
                    message: 'Data Lineage Analysis Complete',
                    description: `Data lineage analyzed using ${dbvendor} dialect. Results are displayed below.`,
                    duration: 4,
                });
            } else {
                throw new Error('No data lineage result returned from server');
            }
        } catch (error: any) {
            let errorMessage = 'Failed to analyze data lineage. Please try again.';
            
            // Handle specific error types
            if (error.message) {
                if (error.message.includes('lineage analysis failed')) {
                    errorMessage = `Data lineage analysis failed for ${dbvendor} dialect. Please check your SQL syntax and try again.`;
                } else if (error.message.includes('parse sql failed')) {
                    errorMessage = `The SQL syntax is invalid or unsupported for ${dbvendor} dialect lineage analysis.`;
                } else if (error.message.includes('too large')) {
                    errorMessage = 'The SQL text is too large for lineage analysis. Consider splitting it into smaller parts.';
                } else {
                    errorMessage = error.message;
                }
            }
            
            notification.error({
                message: 'Data Lineage Error',
                description: errorMessage,
                duration: 5,
            });
        } finally {
            setLineageLoading(prev => ({ ...prev, [tabKey]: false }));
        }
    };

    // Handle CSV Lineage export
    const handleCsvLineage = async (tabKey: string, sqlText: string, database: string) => {
        if (!sqlText.trim()) {
            notification.warning({
                message: 'Warning',
                description: 'SQL text is empty. Please enter some SQL to export lineage as CSV.',
            });
            return;
        }

        // Show which database vendor is being used for CSV lineage export
        const dbvendor = database.startsWith('dbv') ? database : `dbv${database}`;
        notification.info({
            message: 'Exporting Lineage as CSV',
            description: `Using database vendor: ${dbvendor} (detected from record: ${database})`,
            duration: 3,
        });

        setCsvLineageLoading(prev => ({ ...prev, [tabKey]: true }));
        
        try {
            const formData = new FormData();
            formData.append('sqltext', sqlText);
            formData.append('dbvendor', dbvendor);
            
            const result = await exportFullLineageAsCsv(formData);
            
            if (result) {
                setCsvLineageData(prev => ({ ...prev, [tabKey]: result }));
                setCsvLineageVisible(prev => ({ ...prev, [tabKey]: true }));
                
                // Show specific message based on what we received
                const hasCSV = result.csv_content && result.csv_content.trim();
                const csvLines = hasCSV ? result.csv_content.split('\n').length : 0;
                
                notification.success({
                    message: 'CSV Lineage Export Complete',
                    description: hasCSV 
                        ? `CSV lineage exported successfully using ${dbvendor} dialect. ${csvLines} lines of CSV data are displayed below.`
                        : `Lineage exported using ${dbvendor} dialect. Results are displayed below.`,
                    duration: 4,
                });
            } else {
                throw new Error('No CSV lineage result returned from server');
            }
        } catch (error: any) {
            let errorMessage = 'Failed to export lineage as CSV. Please try again.';
            
            // Handle specific error types
            if (error.message) {
                if (error.message.includes('CSV lineage export failed')) {
                    errorMessage = `CSV lineage export failed for ${dbvendor} dialect. Please check your SQL syntax and try again.`;
                } else if (error.message.includes('parse sql failed')) {
                    errorMessage = `The SQL syntax is invalid or unsupported for ${dbvendor} dialect CSV lineage export.`;
                } else if (error.message.includes('too large')) {
                    errorMessage = 'The SQL text is too large for CSV lineage export. Consider splitting it into smaller parts.';
                } else {
                    errorMessage = error.message;
                }
            }
            
            notification.error({
                message: 'CSV Lineage Export Error',
                description: errorMessage,
                duration: 5,
            });
        } finally {
            setCsvLineageLoading(prev => ({ ...prev, [tabKey]: false }));
        }
    };

    // Handle SQL Summary generation
    const handleSummary = async (tabKey: string, sqlText: string, database: string, sqlLength: number) => {
        if (!sqlText.trim()) {
            notification.warning({
                message: 'Warning',
                description: 'SQL text is empty. Please enter some SQL to analyze.',
            });
            return;
        }

        const selectedModel = selectedLlmModel[tabKey];
        if (!selectedModel) {
            notification.warning({
                message: 'Warning',
                description: 'Please select an LLM model before generating summary.',
            });
            return;
        }

        const selectedModelInfo = availableModels.find(m => m.id === selectedModel);
        notification.info({
            message: 'Generating SQL Summary',
            description: `Using ${selectedModelInfo?.name || selectedModel} to analyze SQL from ${database} database.`,
            duration: 3,
        });

        setSummaryLoading(prev => ({ ...prev, [tabKey]: true }));
        
        try {
            const response = await customFetch('/api/sql-collector/generate-summary/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    database_vendor: database,
                    query_size_bytes: sqlLength,
                    sql_query: sqlText,
                    llm_model: selectedModel
                }),
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to generate summary');
            }
            
            const result = await response.json();
            
            if (result.summary) {
                setSummaryData(prev => ({ ...prev, [tabKey]: result.summary }));
                setSummaryVisible(prev => ({ ...prev, [tabKey]: true }));
                notification.success({
                    message: 'SQL Summary Generated',
                    description: `Summary generated successfully using ${selectedModelInfo?.name || selectedModel}.`,
                    duration: 4,
                });
            } else {
                throw new Error('No summary returned from server');
            }
        } catch (error: any) {
            let errorMessage = 'Failed to generate SQL summary. Please try again.';
            
            if (error.message) {
                if (error.message.includes('API key') || error.message.includes('not configured')) {
                    errorMessage = 'OpenRouter API key is not configured. Please configure it in Django admin under LLM > LLM providers.';
                } else if (error.message.includes('quota') || error.message.includes('limit')) {
                    errorMessage = 'API quota exceeded. Please try again later or contact administrator.';
                } else if (error.message.includes('timeout')) {
                    errorMessage = 'Request timeout. The SQL might be too large or complex.';
                } else {
                    errorMessage = error.message;
                }
            }
            
            notification.error({
                message: 'Summary Generation Error',
                description: errorMessage,
                duration: 5,
            });
        } finally {
            setSummaryLoading(prev => ({ ...prev, [tabKey]: false }));
        }
    };

    // Handle copy to clipboard
    const handleCopyToClipboard = async (text: string, description: string) => {
        try {
            await navigator.clipboard.writeText(text);
            notification.success({
                message: 'Copied to Clipboard',
                description: `${description} has been copied to clipboard.`,
                duration: 2,
            });
        } catch (error) {
            // Fallback for older browsers
            try {
                const textArea = document.createElement('textarea');
                textArea.value = text;
                textArea.style.position = 'fixed';
                textArea.style.left = '-999999px';
                textArea.style.top = '-999999px';
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                document.execCommand('copy');
                textArea.remove();
                
                notification.success({
                    message: 'Copied to Clipboard',
                    description: `${description} has been copied to clipboard.`,
                    duration: 2,
                });
            } catch (fallbackError) {
                notification.error({
                    message: 'Copy Failed',
                    description: 'Failed to copy to clipboard. Please select and copy manually.',
                    duration: 3,
                });
            }
        }
    };

    // Helper function to render SQL detail content
    const renderSqlDetailContent = (logDetail: SqlLogDetail) => {
        const tabKey = `detail-${logDetail.id}`;
        // Use edited/formatted SQL if available, otherwise use original from database
        // Note: editedSqlText is local state only - original SQL in DB remains unchanged
        const currentSqlText = editedSqlText[tabKey] ?? logDetail.sqltext;
        const isFormatted = editedSqlText[tabKey] !== undefined;
        
        return (
        <div>
            <Divider orientation="left">
                SQL Text
                {isFormatted && (
                    <span style={{ 
                        marginLeft: '12px', 
                        fontSize: '12px', 
                        color: '#52c41a',
                        fontWeight: 'normal'
                    }}>
                        ✓ Formatted (Original preserved in database)
                    </span>
                )}
            </Divider>
            <div style={{ marginBottom: '20px' }}>
                <TextArea
                    value={currentSqlText}
                    onChange={(e) => setEditedSqlText(prev => ({ ...prev, [tabKey]: e.target.value }))}
                    autoSize={{ minRows: 10, maxRows: 20 }}
                    style={{
                        fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, monospace',
                        fontSize: '12px',
                        lineHeight: '1.4',
                        marginBottom: '12px'
                    }}
                    placeholder="Enter SQL text here..."
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                        {currentSqlText ? (
                            <>
                                Length: {currentSqlText.length.toLocaleString()} characters
                                {isFormatted && (
                                    <span style={{ color: '#52c41a', marginLeft: '8px' }}>
                                        📝 Formatted version (original in DB)
                                    </span>
                                )}
                                {currentSqlText.length > 6000 && (
                                    <span style={{ color: '#ff7f00', marginLeft: '8px' }}>
                                        ⚠️ Large SQL (may take longer to format)
                                    </span>
                                )}
                            </>
                        ) : 'No SQL text'}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <Button
                            type="primary"
                            onClick={() => handleFormatSQL(tabKey, currentSqlText, logDetail.db)}
                            loading={formatLoading[tabKey] || false}
                            style={{ marginBottom: '8px' }}
                        >
                            Format SQL
                        </Button>
                        <Button
                            type="default"
                            onClick={() => handleDataLineage(tabKey, currentSqlText, logDetail.db)}
                            loading={lineageLoading[tabKey] || false}
                            style={{ marginBottom: '8px' }}
                        >
                            Data Lineage
                        </Button>
                        <Button
                            type="default"
                            onClick={() => handleCsvLineage(tabKey, currentSqlText, logDetail.db)}
                            loading={csvLineageLoading[tabKey] || false}
                            style={{ marginBottom: '8px' }}
                        >
                            Data Lineage as CSV
                        </Button>
                        <Select
                            placeholder="Select LLM Model"
                            style={{ width: 250, marginBottom: '8px' }}
                            value={selectedLlmModel[tabKey]}
                            onChange={(value) => setSelectedLlmModel(prev => ({ ...prev, [tabKey]: value }))}
                            size="middle"
                            loading={modelsLoading}
                            disabled={availableModels.length === 0}
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                            }
                        >
                            {availableModels.map(model => (
                                <Option key={model.id} value={model.id} title={`${model.name} (${model.provider})`}>
                                    {model.name} ({model.provider})
                                </Option>
                            ))}
                        </Select>
                        <Button
                            type="default"
                            icon={<FileTextOutlined />}
                            onClick={() => handleSummary(tabKey, currentSqlText, logDetail.db, logDetail.sql_length)}
                            loading={summaryLoading[tabKey] || false}
                            style={{ marginBottom: '8px' }}
                            disabled={!selectedLlmModel[tabKey]}
                        >
                            Summary
                        </Button>
                        <Button
                            type="default"
                            icon={<CopyOutlined />}
                            onClick={() => handleCopyToClipboard(currentSqlText, 'SQL text')}
                            style={{ marginBottom: '8px' }}
                            disabled={!currentSqlText}
                        >
                            Copy SQL
                        </Button>
                        {isFormatted && (
                            <Button
                                type="default"
                                onClick={() => {
                                    setEditedSqlText(prev => {
                                        const newState = { ...prev };
                                        delete newState[tabKey];
                                        return newState;
                                    });
                                    notification.info({
                                        message: 'Reset Complete',
                                        description: 'SQL text has been reset to the original version from database.',
                                        duration: 3,
                                    });
                                }}
                                style={{ marginBottom: '8px' }}
                            >
                                Reset to Original
                            </Button>
                        )}
                    </div>
                </div>
            </div>
            
            {/* Data Lineage Results Section */}
            {lineageVisible[tabKey] && lineageData[tabKey] && (
                <div style={{ marginBottom: '20px' }}>
                    <Collapse
                        activeKey={lineageVisible[tabKey] ? ['1'] : []}
                        onChange={(key) => {
                            setLineageVisible(prev => ({ 
                                ...prev, 
                                [tabKey]: key.includes('1') 
                            }));
                        }}
                        items={[
                            {
                                key: '1',
                                label: (
                                    <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
                                        📊 Data Lineage Results
                                        <span style={{ 
                                            marginLeft: '12px', 
                                            fontSize: '12px', 
                                            color: '#666',
                                            fontWeight: 'normal'
                                        }}>
                                            Click to {lineageVisible[tabKey] ? 'collapse' : 'expand'}
                                        </span>
                                    </span>
                                ),
                                children: (
                                    <div>
            <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'center',
                                            marginBottom: '12px'
                                        }}>
                                            <span style={{ fontSize: '12px', color: '#666' }}>
                                                Lineage analysis results for {logDetail.db} database
                                            </span>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <Button
                                                    size="small"
                                                    icon={<CopyOutlined />}
                                                    onClick={() => {
                                                        const contentToCopy = typeof lineageData[tabKey] === 'string' 
                                                            ? lineageData[tabKey] 
                                                            : JSON.stringify(lineageData[tabKey], null, 2);
                                                        handleCopyToClipboard(contentToCopy, 'Data lineage results');
                                                    }}
                                                >
                                                    Copy
                                                </Button>
                                                <Button
                                                    size="small"
                                                    onClick={() => {
                                                        setLineageVisible(prev => ({ ...prev, [tabKey]: false }));
                                                        setLineageData(prev => {
                                                            const newData = { ...prev };
                                                            delete newData[tabKey];
                                                            return newData;
                                                        });
                                                        notification.info({
                                                            message: 'Data Lineage Cleared',
                                                            description: 'Data lineage results have been cleared.',
                                                            duration: 2,
                                                        });
                                                    }}
                                                >
                                                    Clear Results
                                                </Button>
                                            </div>
                                        </div>
                                        <TextArea
                                            value={typeof lineageData[tabKey] === 'string' 
                                                ? lineageData[tabKey] 
                                                : JSON.stringify(lineageData[tabKey], null, 2)
                                            }
                                            readOnly
                                            autoSize={{ minRows: 6, maxRows: 15 }}
                                            style={{
                        fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, monospace',
                        fontSize: '12px',
                        lineHeight: '1.4',
                                                backgroundColor: '#f8f9fa',
                                                border: '1px solid #e1e5e9'
                                            }}
                                            placeholder="Data lineage results will appear here..."
                                        />
                                    </div>
                                )
                            }
                        ]}
                    />
                </div>
            )}

            {/* CSV Lineage Results Section */}
            {csvLineageVisible[tabKey] && csvLineageData[tabKey] && (
                <div style={{ marginBottom: '20px' }}>
                    <Collapse
                        activeKey={csvLineageVisible[tabKey] ? ['1'] : []}
                        onChange={(key) => {
                            setCsvLineageVisible(prev => ({ 
                                ...prev, 
                                [tabKey]: key.includes('1') 
                            }));
                        }}
                        items={[
                            {
                                key: '1',
                                label: (
                                    <span style={{ fontWeight: 'bold', color: '#52c41a' }}>
                                        📊 Data Lineage CSV Results
                                        <span style={{ 
                                            marginLeft: '12px', 
                                            fontSize: '12px', 
                                            color: '#666',
                                            fontWeight: 'normal'
                                        }}>
                                            Click to {csvLineageVisible[tabKey] ? 'collapse' : 'expand'}
                                        </span>
                                    </span>
                                ),
                                children: (
                                    <div>
                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'center',
                                            marginBottom: '12px'
                                        }}>
                                            <span style={{ fontSize: '12px', color: '#666' }}>
                                                CSV lineage export results for {logDetail.db} database
                                                {csvLineageData[tabKey]?.filename && (
                                                    <span style={{ marginLeft: '8px', fontStyle: 'italic' }}>
                                                        ({csvLineageData[tabKey].filename})
                                                    </span>
                                                )}
                                            </span>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <Button
                                                    size="small"
                                                    icon={<CopyOutlined />}
                                                    onClick={() => {
                                                        const contentToCopy = csvLineageData[tabKey]?.csv_content 
                                                            ? csvLineageData[tabKey].csv_content
                                                            : typeof csvLineageData[tabKey] === 'string' 
                                                                ? csvLineageData[tabKey] 
                                                                : JSON.stringify(csvLineageData[tabKey], null, 2);
                                                        handleCopyToClipboard(contentToCopy, 'CSV lineage results');
                                                    }}
                                                >
                                                    Copy CSV
                                                </Button>
                                                <Button
                                                    size="small"
                                                    onClick={() => {
                                                        setCsvLineageVisible(prev => ({ ...prev, [tabKey]: false }));
                                                        setCsvLineageData(prev => {
                                                            const newData = { ...prev };
                                                            delete newData[tabKey];
                                                            return newData;
                                                        });
                                                        notification.info({
                                                            message: 'CSV Lineage Cleared',
                                                            description: 'CSV lineage results have been cleared.',
                                                            duration: 2,
                                                        });
                                                    }}
                                                >
                                                    Clear Results
                                                </Button>
                                            </div>
                                        </div>
                                        <TextArea
                                            value={
                                                // Handle CSV response - display CSV content directly
                                                csvLineageData[tabKey]?.csv_content 
                                                    ? csvLineageData[tabKey].csv_content
                                                    : typeof csvLineageData[tabKey] === 'string' 
                                                        ? csvLineageData[tabKey] 
                                                        : JSON.stringify(csvLineageData[tabKey], null, 2)
                                            }
                                            readOnly
                                            autoSize={{ minRows: 8, maxRows: 20 }}
                                            style={{
                                                fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, monospace',
                                                fontSize: '11px',
                                                lineHeight: '1.3',
                                                backgroundColor: '#f8f9fa',
                                                border: '1px solid #e1e5e9'
                                            }}
                                            placeholder="CSV lineage results will appear here..."
                                        />
                                    </div>
                                )
                            }
                        ]}
                    />
                </div>
            )}

            {/* Summary Results Section */}
            {summaryVisible[tabKey] && summaryData[tabKey] && (
                <div style={{ marginBottom: '20px' }}>
                    <Collapse
                        activeKey={summaryVisible[tabKey] ? ['1'] : []}
                        onChange={(key) => {
                            setSummaryVisible(prev => ({ 
                                ...prev, 
                                [tabKey]: key.includes('1') 
                            }));
                        }}
                        items={[
                            {
                                key: '1',
                                label: (
                                    <span style={{ fontWeight: 'bold', color: '#722ed1' }}>
                                        📊 SQL Analysis Summary
                                        <span style={{ 
                                            marginLeft: '12px', 
                                            fontSize: '12px', 
                                            color: '#666',
                                            fontWeight: 'normal'
                                        }}>
                                            Generated by {availableModels.find(m => m.id === selectedLlmModel[tabKey])?.name || 'LLM'} - Click to {summaryVisible[tabKey] ? 'collapse' : 'expand'}
                                        </span>
                                    </span>
                                ),
                                children: (
                                    <div>
                                        <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'center',
                                            marginBottom: '12px'
                                        }}>
                                            <span style={{ fontSize: '12px', color: '#666' }}>
                                                SQL analysis for {logDetail.db} database ({logDetail.sql_length.toLocaleString()} bytes)
                                            </span>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <Button
                                                    size="small"
                                                    icon={<CopyOutlined />}
                                                    onClick={() => {
                                                        handleCopyToClipboard(summaryData[tabKey], 'SQL analysis summary');
                                                    }}
                                                >
                                                    Copy Summary
                                                </Button>
                                                <Button
                                                    size="small"
                                                    onClick={() => {
                                                        setSummaryVisible(prev => ({ ...prev, [tabKey]: false }));
                                                        setSummaryData(prev => {
                                                            const newData = { ...prev };
                                                            delete newData[tabKey];
                                                            return newData;
                                                        });
                                                        notification.info({
                                                            message: 'Summary Cleared',
                                                            description: 'SQL analysis summary has been cleared.',
                                                            duration: 2,
                                                        });
                                                    }}
                                                >
                                                    Clear Summary
                                                </Button>
                                            </div>
                                        </div>
                                        <div 
                                            style={{
                                                backgroundColor: '#f8f9fa',
                                                border: '1px solid #e1e5e9',
                                                borderRadius: '6px',
                                                padding: '16px',
                                                fontFamily: 'Monaco, Menlo, "Ubuntu Mono", Consolas, monospace',
                                                fontSize: '12px',
                                                lineHeight: '1.6',
                                                whiteSpace: 'pre-wrap',
                                                maxHeight: '400px',
                                                overflowY: 'auto'
                                            }}
                                            dangerouslySetInnerHTML={{
                                                __html: summaryData[tabKey].replace(/\n/g, '<br/>')
                                                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                                    .replace(/\*(.*?)\*/g, '<em>$1</em>')
                                                    .replace(/`([^`]+)`/g, '<code style="background: #f1f3f4; padding: 2px 4px; border-radius: 3px;">$1</code>')
                                            }}
                                        />
                                    </div>
                                )
                            }
                        ]}
                    />
                </div>
            )}
            
            <Divider orientation="left">SQL Details</Divider>
            <Descriptions bordered column={2} size="small">
                <Descriptions.Item label="Record ID">{logDetail.id}</Descriptions.Item>
                <Descriptions.Item label="MD5 Hash">
                    <Text code>{logDetail.sql_md5}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Source">
                    <Tag color={getSourceColor(logDetail.source)}>
                        {logDetail.source.toUpperCase()}
                    </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Database">
                    <Tag>{logDetail.db}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="SQL Length">{logDetail.sql_length.toLocaleString()} characters</Descriptions.Item>
                <Descriptions.Item label="Count">{logDetail.count}</Descriptions.Item>
                <Descriptions.Item label="Parse Status">
                    <Tag color={getRetvalueColor(logDetail.retvalue)}>
                        {getRetvalueText(logDetail.retvalue)}
                    </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Duration">
                    {logDetail.duration ? `${logDetail.duration.toFixed(2)}ms` : 'N/A'}
                </Descriptions.Item>
                <Descriptions.Item label="Created Time">{formatDateTime(logDetail.create_time)}</Descriptions.Item>
                <Descriptions.Item label="Inserted Time">{formatDateTime(logDetail.insert_time)}</Descriptions.Item>
                <Descriptions.Item label="Updated Time">{formatDateTime(logDetail.update_time)}</Descriptions.Item>
                <Descriptions.Item label="User Auth ID">
                    {logDetail.user_auth_id ? (
                        <Text code>{logDetail.user_auth_id}</Text>
                    ) : 'N/A'}
                </Descriptions.Item>
            </Descriptions>
        </div>
    );
    };

    const handleSearch = async (values: any) => {
        setSearching(true);
        
        // Format date range if provided
        const filters: SearchFilters = {};
        
        // Handle date range conversion
        if (values.create_time_range && values.create_time_range.length === 2) {
            filters.create_time_start = values.create_time_range[0].format('YYYY-MM-DD HH:mm:ss');
            filters.create_time_end = values.create_time_range[1].format('YYYY-MM-DD HH:mm:ss');
        }
        
        // Copy other filter values
        if (values.database) filters.database = values.database;
        if (values.source) filters.source = values.source;
        if (values.sql_length_min) filters.sql_length_min = values.sql_length_min;
        if (values.duration_min) filters.duration_min = values.duration_min;
        if (values.user_auth_id) filters.user_auth_id = values.user_auth_id;

        setCurrentFilters(filters);
        setPagination(prev => ({ ...prev, current: 1 }));
        await fetchSqlLogs(1, pagination.pageSize, filters);
    };

    const handleClearSearch = () => {
        searchForm.resetFields();
        setCurrentFilters({});
        setPagination(prev => ({ ...prev, current: 1 }));
        fetchSqlLogs(1, pagination.pageSize, {});
    };

    const handlePageChange = (page: number, pageSize: number) => {
        if (page < 1) page = 1;
        setPagination(prev => ({ ...prev, current: page, pageSize: pageSize }));
    };

    const formatDateTime = (dateStr: string) => {
        return new Date(dateStr).toLocaleString();
    };

    const getSourceColor = (source: string) => {
        return source === 'sqlflow' ? 'blue' : 'green';
    };

    const getRetvalueColor = (retvalue?: number) => {
        if (retvalue === undefined || retvalue === null) return 'default';
        return retvalue === 0 ? 'success' : 'error';
    };

    const getRetvalueText = (retvalue?: number) => {
        if (retvalue === undefined || retvalue === null) return 'Unknown';
        return retvalue === 0 ? 'Success' : 'Failed';
    };

    const fetchS3Files = async () => {
        const logType = form.getFieldValue('log_type');
        if (!logType) {
            notification.warning({
                message: 'Please select a log type first',
                description: 'You need to select either SQLFlow or SQLFormatter before fetching S3 files.',
            });
            return;
        }

        setS3Loading(true);
        try {
            const response = await customFetch(`/api/sql-collector/s3-files/?log_type=${logType}`);
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to fetch S3 files');
            }
            
            const data: S3FileListResponse = await response.json();
            setS3Files(data.files);
            setSelectedS3File(undefined); // Reset selection
            
            notification.success({
                message: 'S3 Files Fetched',
                description: `Found ${data.files.length} files in ${data.prefix}`,
            });
        } catch (err: any) {
            notification.error({
                message: 'Failed to fetch S3 files',
                description: err.message || 'Could not retrieve files from S3.',
            });
            setS3Files([]);
        } finally {
            setS3Loading(false);
        }
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Load filter options, available models, and SQL logs on component mount
    useEffect(() => {
        fetchFilterOptions();
        fetchAvailableModels();
        fetchSqlLogs(pagination.current, pagination.pageSize, currentFilters);
    }, [fetchFilterOptions, fetchAvailableModels, fetchSqlLogs, pagination.current, pagination.pageSize]);

    // Clean up interval on component unmount
    useEffect(() => {
        return () => {
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
            }
        };
    }, []);

    const pollProgress = async (sessionId: string) => {
        try {
            const response = await customFetch(`/api/sql-collector/progress/${sessionId}/`);
            const progressData: ProgressData = await response.json();

            setProgress(progressData.progress_percentage);
            setProgressMessage(progressData.message);

            if (progressData.status === 'completed') {
                if (progressIntervalRef.current) {
                    clearInterval(progressIntervalRef.current);
                    progressIntervalRef.current = null;
                }
                
                if (progressData.summary) {
                    setSummary(progressData.summary);
                    notification.success({
                        message: 'Processing Complete',
                        description: 'Log file processed successfully.',
                    });
                    // Refresh the SQL logs list after processing
                    fetchSqlLogs(1, pagination.pageSize, currentFilters);
                }
                setUploading(false);
                form.resetFields();
                setFileList([]);
                setCurrentSessionId(null);
            } else if (progressData.status === 'error') {
                if (progressIntervalRef.current) {
                    clearInterval(progressIntervalRef.current);
                    progressIntervalRef.current = null;
                }
                
                notification.error({
                    message: 'Processing Failed',
                    description: progressData.error || 'An error occurred while processing the file.',
                });
                setUploading(false);
                setCurrentSessionId(null);
            }
        } catch (error) {
            console.error('Failed to fetch progress:', error);
        }
    };

    const handleUpload = async (values: { log_type: string }) => {
        // Check if either local file or S3 file is selected
        const hasLocalFile = fileList.length > 0;
        const hasS3File = selectedS3File && selectedS3File.trim() !== '';
        
        if (!hasLocalFile && !hasS3File) {
            notification.error({
                message: 'Upload Error',
                description: 'Please either select a file from S3 or upload a local file.',
            });
            return;
        }
        
        if (hasLocalFile && hasS3File) {
            notification.error({
                message: 'Upload Error',
                description: 'Please select either a local file OR an S3 file, not both.',
            });
            return;
        }

        const formData = new FormData();
        formData.append('log_type', values.log_type);
        
        if (hasS3File) {
            // For S3 files, send the S3 path
            formData.append('s3_file_path', selectedS3File);
        } else {
            // For local files, send the uploaded file
            formData.append('log_file', fileList[0] as any);
        }

        setUploading(true);
        setSummary(null);
        setProgress(0);
        setProgressMessage('Starting processing...');

        try {
            // Start processing with progress tracking
            const response = await customFetch('/api/sql-collector/process-log-with-progress/', {
                method: 'POST',
                body: formData,
            });
            const data = await response.json();

            if (response.ok && data.session_id) {
                setCurrentSessionId(data.session_id);
                
                // Start polling for progress updates
                progressIntervalRef.current = setInterval(() => {
                    pollProgress(data.session_id);
                }, 1000); // Poll every second
                
                // Initial progress check
                pollProgress(data.session_id);
            } else {
                notification.error({
                    message: 'Processing Failed',
                    description: data.error || 'Failed to start processing.',
                });
                setUploading(false);
            }
        } catch (err) {
            notification.error({
                message: 'Processing Failed',
                description: 'An unexpected error occurred.',
            });
            setUploading(false);
        }
    };

    const uploadProps = {
        onRemove: () => {
            setFileList([]);
        },
        beforeUpload: (file: UploadFile) => {
            setFileList([file]);
            // Clear S3 file selection when local file is selected
            setSelectedS3File(undefined);
            return false;
        },
        fileList,
        maxCount: 1,
    };

    const handleS3FileChange = (value: string) => {
        setSelectedS3File(value);
        // Clear local file selection when S3 file is selected
        if (value) {
            setFileList([]);
        }
    };

    // Define tab items
    const tabItems = [
        {
            key: 'main',
            label: 'SQL Collector',
            children: (
                <>
                    <Title level={3} style={{ marginBottom: '12px' }}>SQL Collector</Title>
            <Form
                form={form}
                layout="vertical"
                onFinish={handleUpload}
                initialValues={{ log_type: 'sqlflow' }}
                style={{ maxWidth: 800, marginBottom: '16px' }}
                size="small"
            >
                <Row gutter={12}>
                    <Col span={4}>
                        <Form.Item
                            name="log_type"
                            label="Log Type"
                            rules={[{ required: true, message: 'Please select a log type!' }]}
                            style={{ marginBottom: '8px' }}
                        >
                            <Select>
                                <Option value="sqlflow">SQLFlow</Option>
                                <Option value="sqlformatter">SQLFormatter</Option>
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={4}>
                        <Form.Item label=" " style={{ marginBottom: '8px' }}>
                            <Button 
                                onClick={fetchS3Files}
                                loading={s3Loading}
                                disabled={uploading}
                                size="small"
                            >
                                Fetch S3 Files
                            </Button>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item
                            name="s3_file"
                            label="S3 Files"
                            style={{ marginBottom: '8px' }}
                        >
                            <Select
                                placeholder="Select a file from S3"
                                allowClear
                                disabled={s3Files.length === 0}
                                value={selectedS3File}
                                onChange={handleS3FileChange}
                                popupRender={(menu) => (
                                    <div>
                                        {s3Files.length === 0 ? (
                                            <div style={{ padding: '8px 12px', color: '#999' }}>
                                                No files available. Click "Fetch S3 Files" first.
                                            </div>
                                        ) : (
                                            menu
                                        )}
                                    </div>
                                )}
                            >
                                {s3Files.map(file => (
                                    <Option key={file.full_path} value={file.full_path}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ fontWeight: 'bold' }}>{file.name}</span>
                                            <span style={{ fontSize: '12px', color: '#666' }}>
                                                {formatFileSize(file.size)} - {new Date(file.last_modified).toLocaleDateString()}
                                                {file.file_timestamp && file.file_timestamp !== file.last_modified && 
                                                    ` (File: ${new Date(file.file_timestamp).toLocaleDateString()})`
                                                }
                                            </span>
                                        </div>
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={5}>
                        <Form.Item
                            name="log_file"
                            label="Local File"
                            valuePropName="fileList"
                            getValueFromEvent={(e: any) => {
                                if (Array.isArray(e)) {
                                    return e;
                                }
                                return e && e.fileList;
                            }}
                            style={{ marginBottom: '8px' }}
                        >
                            <Upload {...uploadProps}>
                                <Button icon={<UploadOutlined />} size="small">Select Local</Button>
                            </Upload>
                        </Form.Item>
                    </Col>
                    <Col span={3}>
                        <Form.Item label=" " style={{ marginBottom: '8px' }}>
                            <Button type="primary" htmlType="submit" loading={uploading} disabled={uploading}>
                                {uploading ? 'Processing...' : 'Go'}
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>

            {uploading && (
                <div style={{ marginTop: 12 }}>
                    <Alert
                        message="Processing in Progress"
                        description={progressMessage}
                        type="info"
                        showIcon
                        style={{ marginBottom: 12 }}
                    />
                    <Progress 
                        percent={progress} 
                        status={uploading ? 'active' : 'success'}
                        format={(percent) => `${percent}%`}
                        size="small"
                    />
                    {currentSessionId && (
                        <Text type="secondary" style={{ display: 'block', marginTop: 6, fontSize: '12px' }}>
                            Session ID: {currentSessionId}
                        </Text>
                    )}
                </div>
            )}

            {summary && (
                <div style={{ marginTop: 12 }}>
                    <Title level={5} style={{ marginBottom: '8px' }}>Processing Summary</Title>
                    
                    {/* SQLFormatter specific display */}
                    {summary.log_file_name && (
                        <Alert
                            message={`SQLFormatter Log: ${summary.log_file_name}`}
                            type="info"
                            showIcon
                            style={{ marginBottom: 8 }}
                            description={`Processing completed for ${summary.log_file_name} with ${summary.total_lines?.toLocaleString() || 0} total lines`}
                        />
                    )}
                    
                    <Row gutter={8} style={{ fontSize: '12px' }}>
                        {/* SQLFlow specific fields */}
                        {summary.total_zip_files !== undefined && (
                            <Col span={4}>
                                <Statistic title="Total Zip Files" value={summary.total_zip_files} style={{ fontSize: '12px' }} />
                            </Col>
                        )}
                        {summary.processed_zip_files !== undefined && (
                             <Col span={4}>
                                <Statistic title="Processed Zip Files" value={summary.processed_zip_files} style={{ fontSize: '12px' }} />
                            </Col>
                        )}
                        
                        {/* SQLFormatter specific fields */}
                        {summary.total_lines !== undefined && (
                            <Col span={4}>
                                <Statistic title="Total Lines" value={summary.total_lines.toLocaleString()} style={{ fontSize: '12px' }} />
                            </Col>
                        )}
                        {summary.processed_lines_count !== undefined && (
                            <Col span={4}>
                                <Statistic title="Lines Processed" value={summary.processed_lines_count.toLocaleString()} style={{ fontSize: '12px' }} />
                            </Col>
                        )}
                        {summary.valid_sql_lines !== undefined && (
                            <Col span={4}>
                                <Statistic title="Valid SQL Lines" value={summary.valid_sql_lines.toLocaleString()} style={{ fontSize: '12px' }} />
                            </Col>
                        )}
                        {summary.error_lines_count !== undefined && (
                            <Col span={4}>
                                <Statistic 
                                    title="Error Lines" 
                                    value={summary.error_lines_count.toLocaleString()} 
                                    valueStyle={{ color: summary.error_lines_count > 0 ? '#cf1322' : '#3f8600', fontSize: '12px' }}
                                    style={{ fontSize: '12px' }}
                                />
                            </Col>
                        )}
                        {summary.lines_per_second !== undefined && (
                            <Col span={4}>
                                <Statistic title="Processing Rate" value={summary.lines_per_second} suffix="lines/s" precision={2} style={{ fontSize: '12px' }} />
                            </Col>
                        )}
                        
                        {/* Common fields */}
                        <Col span={4}>
                            <Statistic title="New SQLs" value={summary.new_sqls_count.toLocaleString()} style={{ fontSize: '12px' }} />
                        </Col>
                        <Col span={4}>
                            <Statistic title="Updated SQLs" value={summary.updated_sqls_count.toLocaleString()} style={{ fontSize: '12px' }} />
                        </Col>
                        {summary.total_sql_records !== undefined && (
                            <Col span={4}>
                                <Statistic 
                                    title="Total SQL Records" 
                                    value={summary.total_sql_records.toLocaleString()} 
                                    prefix="∑"
                                    style={{ fontSize: '12px' }}
                                />
                            </Col>
                        )}
                        <Col span={4}>
                            <Statistic title="Duration" value={summary.processing_duration_seconds} suffix="s" precision={2} style={{ fontSize: '12px' }} />
                        </Col>
                    </Row>
                    
                    {summary.processing_start_time && (
                        <div style={{ marginTop: 8 }}>
                            <Alert
                                message="Processing Details"
                                description={
                                    <div style={{ fontSize: '12px' }}>
                                        <p><strong>Processing Started:</strong> {new Date(summary.processing_start_time).toLocaleString()}</p>
                                        <p><strong>Total SQL Records:</strong> This represents the sum of the 'count' field for all SQL records inserted or updated in this processing round.</p>
                                        
                                        {/* SQLFlow verification */}
                                        {summary.total_zip_files && summary.total_sql_records && (
                                            <p><strong>SQLFlow Verification:</strong> Total zip files processed ({summary.total_zip_files.toLocaleString()}) should equal total SQL records ({summary.total_sql_records.toLocaleString()}) as each zip file contains one SQL statement.</p>
                                        )}
                                        
                                        {/* SQLFormatter verification */}
                                        {summary.log_file_name && summary.valid_sql_lines && summary.total_sql_records && (
                                            <p><strong>SQLFormatter Verification:</strong> Valid SQL lines ({summary.valid_sql_lines.toLocaleString()}) processed from {summary.log_file_name}, resulting in {summary.total_sql_records.toLocaleString()} total SQL records (including duplicates).</p>
                                        )}
                                        
                                        {/* Processing efficiency */}
                                        {summary.lines_per_second && (
                                            <p><strong>Processing Efficiency:</strong> {summary.lines_per_second.toLocaleString()} lines processed per second.</p>
                                        )}
                                    </div>
                                }
                                type="info"
                                showIcon
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Search and Filter Section */}
            <Divider style={{ margin: '12px 0' }} />
            <div style={{ marginBottom: '12px' }}>
                <Title level={5} style={{ marginBottom: '8px' }}>Search & Filter SQL Logs</Title>
                <Card size="small" style={{ marginBottom: '8px', padding: '8px' }}>
                    <Form
                        form={searchForm}
                        layout="vertical"
                        onFinish={handleSearch}
                        size="small"
                    >
                        <Row gutter={8}>
                            <Col span={6}>
                                <Form.Item
                                    name="create_time_range"
                                    label="Time Range"
                                    style={{ marginBottom: '8px' }}
                                >
                                    <RangePicker
                                        showTime={{ format: 'HH:mm:ss' }}
                                        format="YYYY-MM-DD HH:mm:ss"
                                        style={{ width: '100%' }}
                                        placeholder={['Start', 'End']}
                                        size="small"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={3}>
                                <Form.Item
                                    name="database"
                                    label="Database"
                                    style={{ marginBottom: '8px' }}
                                >
                                    <Select
                                        placeholder="Database"
                                        allowClear
                                        showSearch
                                        size="small"
                                        filterOption={(input, option) =>
                                            (option?.children as unknown as string)?.toLowerCase().includes(input.toLowerCase())
                                        }
                                    >
                                        {filterOptions.databases.map(db => (
                                            <Option key={db} value={db}>{db}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={3}>
                                <Form.Item
                                    name="source"
                                    label="Source"
                                    style={{ marginBottom: '8px' }}
                                >
                                    <Select
                                        placeholder="Source"
                                        allowClear
                                        size="small"
                                    >
                                        {filterOptions.sources.map(source => (
                                            <Option key={source} value={source}>{source}</Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={3}>
                                <Form.Item
                                    name="sql_length_min"
                                    label="Min Length"
                                    style={{ marginBottom: '8px' }}
                                >
                                    <InputNumber
                                        placeholder="Characters"
                                        min={0}
                                        style={{ width: '100%' }}
                                        size="small"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={3}>
                                <Form.Item
                                    name="duration_min"
                                    label="Min Duration"
                                    style={{ marginBottom: '8px' }}
                                >
                                    <InputNumber
                                        placeholder="ms"
                                        min={0}
                                        step={0.1}
                                        style={{ width: '100%' }}
                                        size="small"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={4}>
                                <Form.Item
                                    name="user_auth_id"
                                    label="User Auth ID"
                                    style={{ marginBottom: '8px' }}
                                >
                                    <Input
                                        placeholder="Auth ID"
                                        allowClear
                                        size="small"
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={2}>
                                <Form.Item label=" " style={{ marginBottom: '8px' }}>
                                    <Space size="small">
                                        <Button 
                                            type="primary" 
                                            htmlType="submit" 
                                            icon={<SearchOutlined />}
                                            loading={searching}
                                            size="small"
                                        >
                                            Search
                                        </Button>
                                        <Button 
                                            onClick={handleClearSearch}
                                            icon={<ClearOutlined />}
                                            size="small"
                                        >
                                            Clear
                                        </Button>
                                    </Space>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Card>
            </div>

            {/* SQL Logs Records Section */}
            <div>
                <Title level={5} style={{ marginBottom: '8px' }}>
                    SQL Logs Records 
                    {pagination.total > 0 && (
                        <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#666' }}>
                            ({pagination.total.toLocaleString()} records found)
                        </span>
                    )}
                </Title>
                {logsLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <Spin size="large" />
                    </div>
                ) : logsError ? (
                    <Alert message={logsError} type="warning" showIcon />
                ) : (
                    <>
                        <List
                            size="small"
                            dataSource={sqlLogs}
                            renderItem={(item, index) => (
                                <List.Item 
                                    style={{ padding: '6px 0' }}
                                    actions={[
                                        <Button 
                                            type="link" 
                                            icon={<EyeOutlined />} 
                                            onClick={() => handleLogClick(item)}
                                            size="small"
                                        >
                                            View Details
                                        </Button>
                                    ]}
                                >
                                    <List.Item.Meta
                                        title={
                                            <div style={{ 
                                                fontSize: '13px', 
                                                fontWeight: 'normal',
                                                color: '#262626'
                                            }}>
                                                <span style={{ marginRight: '12px' }}>
                                                    <Text strong>#{((pagination.current - 1) * pagination.pageSize) + index + 1}</Text>
                                                </span>
                                                <span style={{ marginRight: '12px' }}>
                                                    <Text strong>Created:</Text> {formatDateTime(item.create_time)}
                                                </span>
                                                <span style={{ marginRight: '12px' }}>
                                                    <Text strong>Database:</Text> <Tag>{item.db}</Tag>
                                                </span>
                                                <span>
                                                    <Text strong>Length:</Text> {item.sql_length.toLocaleString()} chars
                                                </span>
                                            </div>
                                        }
                                    />
                                </List.Item>
                            )}
                        />
                        
                        {pagination.total > 0 && (
                            <div style={{ textAlign: 'center', marginTop: 8 }}>
                                <Pagination
                                    size="small"
                                    current={pagination.current}
                                    total={pagination.total}
                                    pageSize={pagination.pageSize}
                                    showSizeChanger
                                    showQuickJumper
                                    showTotal={(total, range) => 
                                        `${range[0]}-${range[1]} of ${total} records`
                                    }
                                    onChange={handlePageChange}
                                    onShowSizeChange={handlePageChange}
                                    pageSizeOptions={['10', '20', '50', '100']}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>

                </>
            )
        },
        // Add detail tabs dynamically
        ...tabPanes.map(pane => ({
            key: pane.key,
            label: pane.title,
            closable: true,
            children: pane.loading ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Spin size="large" />
                </div>
            ) : pane.content ? (
                renderSqlDetailContent(pane.content)
            ) : null
        }))
    ];

    return (
        <Card>
            <Tabs
                type="editable-card"
                activeKey={activeTabKey}
                onChange={handleTabChange}
                onEdit={(targetKey, action) => {
                    if (action === 'remove') {
                        handleTabClose(targetKey as string);
                    }
                }}
                items={tabItems}
                hideAdd={true}
            />
        </Card>
    );
};

export default SqlCollectorPage; 